/**
 * Client factory — ties the reducer-only store to a WS transport. The same
 * module powers the browser bundle and the headless full-stack smoke (which
 * injects `ws`'s WebSocket via `WebSocketImpl`).
 *
 * M8-B fills in `store.ts`/`connection.ts`; this wiring stays stable so
 * `App.tsx`/`main.tsx` and the smoke depend on a fixed surface.
 */
import { connectWebSocket, type Transport, type WebSocketCtor } from './connection.js';
import { createClientStore, type ClientStore } from './store.js';

export type { ClientStore, ClientStoreOptions } from './store.js';
export type { ConnectOptions, Transport, WebSocketCtor, WebSocketLike } from './connection.js';

export interface Client {
  store: ClientStore;
  transport: Transport;
}

export interface CreateClientOptions {
  /** Full WS URL, e.g. `ws://host:port/ws?room=r1&playerId=A`. */
  url: string | (() => string);
  /** Inject a WebSocket constructor (default: browser global). */
  WebSocketImpl?: WebSocketCtor;
  onRejectedHandshake?: (attemptUrl: string) => void;
  reconnectDelaysMs?: readonly number[];
  /** Browser-only stale-bundle probe run before an abnormal reconnect. */
  beforeReconnect?: () => Promise<boolean>;
  /** Public display name announced again after every successful session bind. */
  nickname?: string;
}

export function createClient(opts: CreateClientOptions): Client {
  // Holder breaks the store↔transport init cycle without a reassigned `let`.
  const holder: { transport?: Transport } = {};
  const store = createClientStore({
    send: (msg) => {
      if (holder.transport === undefined) throw new Error('createClient: transport ещё не готов');
      holder.transport.send(msg);
    },
  });
  holder.transport = connectWebSocket({
    url: opts.url,
    ...(opts.WebSocketImpl !== undefined ? { WebSocketImpl: opts.WebSocketImpl } : {}),
    ...(opts.onRejectedHandshake !== undefined ? { onRejectedHandshake: opts.onRejectedHandshake } : {}),
    ...(opts.reconnectDelaysMs !== undefined ? { reconnectDelaysMs: opts.reconnectDelaysMs } : {}),
    ...(opts.beforeReconnect !== undefined ? { beforeReconnect: opts.beforeReconnect } : {}),
    onMessage: (msg) => {
      store.ingest(msg);
      if (msg.t === 'session' && opts.nickname !== undefined && opts.nickname.length > 0) {
        holder.transport?.send({ t: 'set_nickname', nickname: opts.nickname });
      }
    },
    onReconnecting: (code, reason, delayMs) => store.notifyReconnecting(code, reason, delayMs),
    // Surface transport failures/disconnects instead of freezing on a stale board.
    onClose: (code, reason) => store.notifyClosed(code, reason),
    onError: (err) => store.notifyError(err instanceof Error ? err.message : 'ошибка соединения'),
  });
  return { store, transport: holder.transport };
}
